import api from './api';
import ErrorHandler from './errorHandler';

class AiTemplateService {
    async list(params = {}) {
        try {
            const response = await api.get('/api/ai-templates', { params });
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    async categories() {
        try {
            const response = await api.get('/api/ai-templates/categories');
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    async tags() {
        try {
            const response = await api.get('/api/ai-templates/tags');
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    // Current user's favorite template ids (array of numbers).
    async favorites() {
        try {
            const response = await api.get('/api/ai-templates/favorites');
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    // Toggle a template in the current user's favorites → { templateId, isFavorite }.
    async toggleFavorite(templateId) {
        try {
            const response = await api.post('/api/ai-templates/favorites/toggle', { templateId });
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    // Admin — AI-generated social caption for a template (promo) → { caption }.
    async generateShareCaption({ templateId, language = 'bn', mode = 'auto' }) {
        try {
            const response = await api.post('/api/ai-templates/share-caption', { templateId, language, mode });
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    // Admin — list generated results that users marked as negative feedback.
    async negativeFeedback(params = {}) {
        try {
            const response = await api.get('/api/ai-templates/negative-feedback', { params });
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    // Admin — mark a negative-feedback report as solved (or reopen with resolved:false).
    async resolveNegativeFeedback(id, resolved = true) {
        try {
            const response = await api.patch(`/api/ai-templates/negative-feedback/${id}/resolve`, { resolved });
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    // Admin — reject a negative-feedback report (won't fix), or un-reject with rejected:false.
    async rejectNegativeFeedback(id, rejected = true) {
        try {
            const response = await api.patch(`/api/ai-templates/negative-feedback/${id}/reject`, { rejected });
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    async get(id) {
        try {
            const response = await api.get(`/api/ai-templates/${id}`);
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    async create(formData) {
        try {
            const response = await api.post('/api/ai-templates', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    async update(id, formData) {
        try {
            const response = await api.put(`/api/ai-templates/${id}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    async remove(id) {
        try {
            const response = await api.delete(`/api/ai-templates/${id}`);
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    async duplicate(id) {
        try {
            const response = await api.post(`/api/ai-templates/${id}/duplicate`);
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    async generate(id, formData) {
        try {
            const response = await api.post(`/api/ai-templates/${id}/generate`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    async analyzePrompt(prompt, categories = [], thumbnailUrl = null, guidance = null) {
        try {
            const body = { prompt, categories, thumbnailUrl };
            if (guidance) body.guidance = guidance;
            const response = await api.post('/api/ai-templates/analyze-prompt', body);
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    async analyzeImage(imageFileOrFiles, categories = [], note = '', mode = '', genders = [], mergedCount = 0, fieldTypes = []) {
        try {
            const fd = new FormData();
            // Accept a single File or an array of Files. The first becomes `image`,
            // the rest `image_2`, `image_3`, … (same convention as generation).
            const files = (Array.isArray(imageFileOrFiles) ? imageFileOrFiles : [imageFileOrFiles]).filter(Boolean);
            files.forEach((f, i) => fd.append(i === 0 ? 'image' : `image_${i + 1}`, f));
            fd.append('categories', JSON.stringify(categories));
            // When the caller pre-merged several photos into ONE grid image, this is
            // how many photos the composite holds — the backend prompt then treats
            // the grid panels as the separate example images.
            if (mergedCount > 1) fd.append('mergedCount', String(mergedCount));
            // Optional admin note to steer the analysis (empty string when unused).
            if (note) fd.append('note', note);
            // 'product' → backend uses the product-ad template prompt instead of portrait.
            if (mode) fd.append('mode', mode);
            // Target audience genders (male/female/children) → gender-aware prompt.
            if (Array.isArray(genders) && genders.length) fd.append('genders', JSON.stringify(genders));
            // Admin-picked field kinds (dress-color, background-change, …) the
            // analyzer must turn into dynamic fields when applicable.
            if (Array.isArray(fieldTypes) && fieldTypes.length) fd.append('fieldTypes', JSON.stringify(fieldTypes));
            const response = await api.post('/api/ai-templates/analyze-image', fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    async referenceImage(url) {
        try {
            // Returns a Blob (api interceptor returns response.data). Proxied server-side
            // to dodge CORS on files.sohozkaj.com.
            const blob = await api.get('/api/ai-templates/reference-image', {
                params: { url },
                responseType: 'blob',
            });
            return blob;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    async enhancePrompt(thumbnailFile, currentPrompt, negativePrompt = '', existingKeys = [], note = '', mode = '') {
        try {
            const fd = new FormData();
            fd.append('image', thumbnailFile);
            fd.append('currentPrompt', currentPrompt || '');
            fd.append('negativePrompt', negativePrompt || '');
            fd.append('existingKeys', JSON.stringify(existingKeys || []));
            if (note) fd.append('note', note);
            // 'fix-issue' → surgical repair of one complaint instead of full enrichment.
            if (mode) fd.append('mode', mode);
            const response = await api.post('/api/ai-templates/enhance-prompt', fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    async syncField(prompt, key, label, type) {
        try {
            const response = await api.post('/api/ai-templates/sync-field', { prompt, key, label, type });
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    async addField(prompt, instruction, existingKeys = []) {
        try {
            const response = await api.post('/api/ai-templates/add-field', { prompt, instruction, existingKeys });
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    async suggestChoice(prompt, key, label, type, existingChoices = []) {
        try {
            const response = await api.post('/api/ai-templates/suggest-choice', { prompt, key, label, type, existingChoices });
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    async optimizePrompt(currentPrompt, optimizationHint) {
        try {
            const response = await api.post('/api/ai-templates/optimize-prompt', { currentPrompt, optimizationHint });
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    async tokenizePrompt(prompt, existingKeys = []) {
        try {
            const response = await api.post('/api/ai-templates/tokenize-prompt', { prompt, existingKeys });
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    async trackUsage(id) {
        try {
            const response = await api.post(`/api/ai-templates/${id}/track-usage`);
            return response;
        } catch (error) {
            // Tracking is best-effort — never throw to the caller.
            console.warn('trackUsage failed', error);
            return null;
        }
    }
}

export default new AiTemplateService();
